import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useTeam } from "@/context/TeamContext";
import { ArrowLeft, UploadSimple, ShieldChevron } from "@phosphor-icons/react";
import { toast } from "sonner";

// Module-scoped so framer-motion isn't handed a brand new object literal
// (and therefore treated as a changed prop) on every render of this page.
const PAGE_FADE_IN = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };


export default function TeamSetup() {
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [season, setSeason] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { teams, setCurrentTeamId, refreshTeams } = useTeam();
  const isFirst = teams.length === 0;

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/teams", { name, age_group: ageGroup, season });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/teams/${data.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await refreshTeams();
      setCurrentTeamId(data.id);
      toast.success("Team created!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain relative flex items-center justify-center p-6">
      <motion.div
        {...PAGE_FADE_IN}
        className="w-full max-w-lg relative z-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo-icon.png" alt="The Gaffer" className="w-12 h-12 object-contain" />
          <span className="font-heading text-3xl text-white pt-1">THE <span className="text-[#CCFF00]">GAFFER</span></span>
        </div>

        {!isFirst && (
          <button onClick={() => navigate("/dashboard")} data-testid="setup-back" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors duration-150 mb-4 font-label text-xs">
            <ArrowLeft size={16} weight="bold" /> Back to dashboard
          </button>
        )}

        <h1 className="font-heading text-5xl text-white mb-2">{isFirst ? "Set up your team" : "Add a team"}</h1>
        <p className="text-zinc-500 mb-8 text-sm">Enter the basics. You can change these anytime.</p>

        <form onSubmit={submit} className="space-y-6 bg-[#121212] border border-zinc-800 p-6 sm:p-8 rounded-sm">
          <div className="flex items-center gap-4">
            <label htmlFor="logo-upload" className="w-20 h-20 shrink-0 border border-dashed border-zinc-700 rounded-sm flex items-center justify-center cursor-pointer hover:border-[#CCFF00] transition-colors duration-150 overflow-hidden bg-[#0A0A0A]" data-testid="logo-upload-label">
              {logoPreview ? <img src={logoPreview} alt="logo preview" className="w-full h-full object-cover" /> : <ShieldChevron size={28} className="text-zinc-600" />}
            </label>
            <div>
              <p className="font-label text-xs text-zinc-400 mb-1 flex items-center gap-2">
                <UploadSimple size={14} /> Team logo
              </p>
              <p className="text-xs text-zinc-600">Optional. PNG or JPG.</p>
              <input id="logo-upload" type="file" accept="image/*" onChange={onLogoChange} data-testid="logo-input" className="hidden" />
            </div>
          </div>

          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Team name</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name-input" placeholder="Riverside United U12" className="w-full bg-[#0A0A0A] border border-zinc-800 text-white px-4 py-3 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Age group</label>
              <input type="text" required value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} data-testid="team-age-input" placeholder="U12" className="w-full bg-[#0A0A0A] border border-zinc-800 text-white px-4 py-3 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150" />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Season</label>
              <input type="text" required value={season} onChange={(e) => setSeason(e.target.value)} data-testid="team-season-input" placeholder="Fall 2025" className="w-full bg-[#0A0A0A] border border-zinc-800 text-white px-4 py-3 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150" />
            </div>
          </div>

          {error && <p className="text-[#FF3B30] text-sm" data-testid="setup-error">{error}</p>}

          <button type="submit" disabled={submitting} data-testid="team-setup-submit" className="w-full bg-[#CCFF00] text-black font-label py-3 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-50">
            {submitting ? "Creating…" : isFirst ? "Create team" : "Add team"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
